/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { useFormContext } from "react-hook-form";

import type { PaymentFields } from "containers/streaming/StreamingLiveCommerceV2";
import type { FieldErrors } from "react-hook-form";

type Props = {
  isDarkModeEnabled: boolean;
};

type CvsErrors = FieldErrors<PaymentFields & { paymentType: "CVS" }>;

export const StreamingLiveCommerceConveniencePaymentV2: React.FC<Props> = (
  props
) => {
  const {
    register,
    watch,
    formState: { errors },
  } = useFormContext<PaymentFields>();
  const selectedCvsCode = watch("selectedCvsCode");
  const paymentType = watch("paymentType");
  const cvsError = errors as CvsErrors;
  const { t } = useI18n();
  const classesConveniencePaymentDesign = conveniencePaymentDesign({
    isDarkModeEnabled: props.isDarkModeEnabled,
  });

  return (
    <React.Fragment>
      <div css={classesConveniencePaymentDesign.container}>
        <div css={classesConveniencePaymentDesign.radioInputContainer}>
          <div css={classesConveniencePaymentDesign.selectionBox}>
            <input
              type="radio"
              id="csv-10001"
              css={classesConveniencePaymentDesign.radioInput}
              checked={selectedCvsCode === "10001"}
              value={"10001"}
              {...register("selectedCvsCode")}
            />
            <label
              htmlFor="csv-10001"
              css={classesConveniencePaymentDesign.radioLabel}
            >
              <img
                src="/images/liveCommerce/paymentConvenienceLawson.png"
                alt="Lawson"
                css={classesConveniencePaymentDesign.brandImage}
              />
              {t("streaming.commerceConveniencePayment.lawson")}
            </label>
          </div>
          <div css={classesConveniencePaymentDesign.selectionBox}>
            <input
              type="radio"
              id="csv-10002"
              css={classesConveniencePaymentDesign.radioInput}
              checked={selectedCvsCode === "10002"}
              value={"10002"}
              {...register("selectedCvsCode")}
            />
            <label
              htmlFor="csv-10002"
              css={classesConveniencePaymentDesign.radioLabel}
            >
              <img
                src="/images/liveCommerce/paymentConvenienceFamilyMart.png"
                alt="FamilyMart"
                css={classesConveniencePaymentDesign.brandImage}
              />
              {t("streaming.commerceConveniencePayment.familyMart")}
            </label>
          </div>
          <div css={classesConveniencePaymentDesign.selectionBox}>
            <input
              type="radio"
              id="csv-10008"
              css={classesConveniencePaymentDesign.radioInput}
              checked={selectedCvsCode === "10008"}
              value={"10008"}
              {...register("selectedCvsCode")}
            />
            <label
              htmlFor="csv-10008"
              css={classesConveniencePaymentDesign.radioLabel}
            >
              <img
                src="/images/liveCommerce/paymentConvenienceSeicomart.png"
                alt="Seicomart"
                css={classesConveniencePaymentDesign.brandImage}
              />
              {t("streaming.commerceConveniencePayment.seicoMart")}
            </label>
          </div>
        </div>
        <div css={classesConveniencePaymentDesign.textInputContainer}>
          <p css={classesConveniencePaymentDesign.subLabel}>
            {t("streaming.commerceConveniencePayment.customerInformation")}
          </p>

          <div css={classesConveniencePaymentDesign.inputGroup}>
            <label htmlFor="customerName">
              {t("streaming.commerceConveniencePayment.name")}
            </label>
            <input
              type="text"
              placeholder="山田太郎"
              id="customerName"
              css={classesConveniencePaymentDesign.textInput}
              {...register("customerName")}
            />
            {paymentType === "CVS" && cvsError.customerName && (
              <span css={classesConveniencePaymentDesign.errorText}>
                {cvsError.customerName.message}
              </span>
            )}
          </div>
          <div css={classesConveniencePaymentDesign.inputGroup}>
            <label htmlFor="customerKana">
              {t("streaming.commerceConveniencePayment.kana")}
            </label>
            <input
              type="text"
              placeholder="ヤマダタロウ"
              id="customerKana"
              css={classesConveniencePaymentDesign.textInput}
              {...register("customerKana")}
            />
            {paymentType === "CVS" && cvsError.customerKana && (
              <span css={classesConveniencePaymentDesign.errorText}>
                {cvsError.customerKana.message}
              </span>
            )}
          </div>
          <div css={classesConveniencePaymentDesign.inputGroup}>
            <label htmlFor="telNo">
              {t("streaming.commerceConveniencePayment.phoneV2")}
            </label>
            <input
              type="text"
              placeholder="01234567890"
              id="telNo"
              css={classesConveniencePaymentDesign.textInput}
              {...register("telNo")}
            />
            {cvsError.telNo && (
              <span css={classesConveniencePaymentDesign.errorText}>
                {cvsError.telNo.message}
              </span>
            )}
          </div>
        </div>
      </div>
    </React.Fragment>
  );
};

type ConveniencePaymentStylesProps = {
  isDarkModeEnabled: boolean;
};

const conveniencePaymentDesign = (props: ConveniencePaymentStylesProps) => {
  const theme = {
    text: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
    bg: props.isDarkModeEnabled ? "#000000" : "#FFFFFF",
    inputBg: props.isDarkModeEnabled ? "#1A1A1A" : "#F0F0F0",
    border: props.isDarkModeEnabled ? "#FFFFFF" : "#000000",
  };

  const container = css`
    margin: 0 16px 0 16px;
    border-top: 1px solid ${theme.border};
  `;
  const subLabel = css`
    color: ${theme.text};
    margin-bottom: 16px;
    font-size: 14px;
  `;
  const selectionBox = css`
    margin-bottom: 16px;
  `;
  const radioInputContainer = css`
    padding-top: 16px;
    padding-left: 16px;
  `;
  const radioInput = css`
    display: none;
    &:checked + label::before {
      background-color: ${theme.text};
    }
    &:checked + label::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 29px;
      transform: translateY(-50%);
      width: 6px;
      height: 6px;
      background-color: ${theme.bg};
      border-radius: 50%;
    }
  `;
  const radioLabel = css`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    padding: 16px 0 16px 56px;
    color: ${theme.text};
    gap: 16px;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    box-sizing: border-box;
    &::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      border: 2px solid ${theme.border};
      border-radius: 50%;
      box-sizing: border-box;
    }
  `;
  const textInputContainer = css`
    padding: 0 24px 0 24px;
  `;
  const inputGroup = css`
    margin-bottom: 16px;
    label {
      display: block;
      font-size: 12px;
      margin-bottom: 8px;
      color: ${theme.text};
    }
  `;
  const textInput = css`
    &[type="text"] {
      background-color: ${theme.bg};
      padding: 0px 16px;
      border-radius: 10px;
      height: 40px;
    }
    width: 100%;
    border: none;
    color: ${theme.text};
    font-size: 16px;
    &::placeholder {
      color: #a3a3a3;
    }
  `;

  const brandImage = css`
    height: 11px;
    width: 80px;
    object-fit: contain;
  `;

  const errorText = css`
    color: red;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
  `;

  return {
    container,
    subLabel,
    selectionBox,
    radioInputContainer,
    radioInput,
    radioLabel,
    textInputContainer,
    inputGroup,
    textInput,
    brandImage,
    errorText,
  };
};
